/* eslint-disable no-console */
import { useState, useEffect } from 'react';
import { NavList } from './nav_item';
import { getState, subscribe, toggle } from './sidebar_state';
import SidebarOpenContext from './sidebar_context';
import {
  getLearnerNavConfig,
  getTrainerNavConfig,
  getManagerNavConfig,
  getGroupLeaderNavConfig,
  getAdminDefaultNavConfig,
  getAdminPartnerNavConfig,
} from './nav_config';

// ─── Role resolution ─────────────────────────────────────────────────────────

const VALID_VARIANTS = new Set([
  'learner',
  'trainer',
  'manager',
  'group_leader',
  'admin_default',
  'admin_partner',
]);

const resolveNavVariant = ({ role, zone, isDefaultPortal, isImpersonating }) => {
  if (isImpersonating) return 'learner';
  if (role === 'admin') {
    if (zone === 'group_leader') return 'group_leader';
    return isDefaultPortal ? 'admin_default' : 'admin_partner';
  }
  if (!VALID_VARIANTS.has(role)) {
    console.error(`[AppSidebar] unexpected role: "${role}", falling back to learner`);
    return 'learner';
  }
  return role;
};

// ─── Per-role nav renderers ──────────────────────────────────────────────────

// NavList handles the accordion: only one item open at a time.
// Extra props (className, data-purpose, etc.) are forwarded to the <ul>.
const renderList = (items, currentPath, activeStates, activeNavKey, ulProps = {}) => (
  <NavList
    items={items}
    currentPath={currentPath}
    activeStates={activeStates}
    activeNavKey={activeNavKey}
    {...ulProps}
  />
);

const LearnerNav = ({
  routes,
  permissions,
  currentPath,
  activeStates,
  activeNavKey = null,
  logoUrl,
  logoHref,
}) => {
  const items = getLearnerNavConfig(routes, permissions);
  return (
    <div className="top-bar__menu">
      {renderList(items, currentPath, activeStates, activeNavKey, { className: 'menu menu-nav' })}
      <div className="logo-learner">
        <div className="top-bar__logo">
          <a href={logoHref} className="logo" aria-label="home">
            <img src={logoUrl} alt="website logo" />
          </a>
        </div>
      </div>
    </div>
  );
};

const TrainerNav = ({
  routes,
  permissions = {},
  currentPath,
  activeStates = {},
  activeNavKey = null,
}) => {
  const items = getTrainerNavConfig(routes, permissions);
  return (
    <div className="top-bar__menu">
      {renderList(items, currentPath, activeStates, activeNavKey, {
        className: 'menu menu-nav',
        'data-purpose': 'list_trainings_container',
      })}
    </div>
  );
};

const ManagerNav = ({
  routes,
  permissions = {},
  currentPath,
  activeStates = {},
  activeNavKey = null,
}) => {
  const items = getManagerNavConfig(routes, permissions);
  return renderList(items, currentPath, activeStates, activeNavKey, { className: 'menu menu-nav' });
};

const GroupLeaderNav = ({ routes, currentPath, activeStates = {}, activeNavKey = null }) => {
  const items = getGroupLeaderNavConfig(routes);
  return renderList(items, currentPath, activeStates, activeNavKey, { className: 'menu menu-nav' });
};

const AdminDefaultNav = ({
  routes,
  permissions = {},
  currentPath,
  activeStates = {},
  activeNavKey = null,
}) => {
  const items = getAdminDefaultNavConfig(routes, permissions);
  return renderList(items, currentPath, activeStates, activeNavKey, {
    className: 'menu menu-admin menu-nav',
  });
};

const AdminPartnerNav = ({
  routes,
  permissions = {},
  currentPath,
  activeStates = {},
  activeNavKey = null,
}) => {
  const items = getAdminPartnerNavConfig(routes, permissions);
  return renderList(items, currentPath, activeStates, activeNavKey, {
    className: 'menu menu-admin menu-nav',
  });
};

const _renderNavContent = (variant, navProps) => {
  switch (variant) {
    case 'learner':
      return <LearnerNav {...navProps} />;
    case 'trainer':
      return <TrainerNav {...navProps} />;
    case 'manager':
      return <ManagerNav {...navProps} />;
    case 'group_leader':
      return <GroupLeaderNav {...navProps} />;
    case 'admin_default':
      return <AdminDefaultNav {...navProps} />;
    case 'admin_partner':
      return <AdminPartnerNav {...navProps} />;
    default:
      return null;
  }
};

AdminPartnerNav.defaultProps = { permissions: {}, activeStates: {} };

// ─── Nav content switcher (must be defined outside AppSidebar so React sees a
//     stable component type across re-renders and never unmounts the nav tree) ──

const NavContent = ({
  variant,
  routes,
  permissions,
  currentPath,
  activeStates,
  activeNavKey,
  logoUrl,
  logoHref,
}) => {
  const navProps = {
    routes,
    permissions,
    currentPath,
    activeStates,
    activeNavKey,
    logoUrl,
    logoHref,
  };
  switch (variant) {
    case 'learner':
      return <LearnerNav {...navProps} />;
    case 'trainer':
      return <TrainerNav {...navProps} />;
    case 'manager':
      return <ManagerNav {...navProps} />;
    case 'group_leader':
      return <GroupLeaderNav {...navProps} />;
    case 'admin_default':
      return <AdminDefaultNav {...navProps} />;
    case 'admin_partner':
      return <AdminPartnerNav {...navProps} />;
    default:
      return null;
  }
};

// ─── Main component ──────────────────────────────────────────────────────────

const AppSidebar = ({
  role,
  zone = null,
  isDefaultPortal = true,
  isImpersonating = false,
  currentPath,
  routes,
  permissions = {},
  activeStates = {},
  activeNavKey = null,
  logoUrl = null,
  logoHref = '/',
}) => {
  const [isOpen, setIsOpen] = useState(getState);

  useEffect(() => {
    const unsubscribe = subscribe(setIsOpen);
    return unsubscribe;
  }, []);

  const variant = resolveNavVariant({ role, zone, isDefaultPortal, isImpersonating });

  return (
    <SidebarOpenContext.Provider value={isOpen}>
      <nav id="skiptonav" className={`sidenav${isOpen ? ' is-active' : ''}`}>
        <div
          className={`nav-icon js-sidenav-toggle${isOpen ? ' is-active' : ''}`}
          data-purpose="sidenav_toggle_button"
          role="button"
          tabIndex={0}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggle();
          }}
        >
          <span />
          <span />
          <span />
        </div>
        <NavContent
          variant={variant}
          routes={routes}
          permissions={permissions}
          currentPath={currentPath}
          activeStates={activeStates}
          activeNavKey={activeNavKey}
          logoUrl={logoUrl}
          logoHref={logoHref}
        />
      </nav>
    </SidebarOpenContext.Provider>
  );
};

export default AppSidebar;
